import { useMemo } from 'react';
import type { Rect } from '@view-model/models/common/basic/Rect';
import { useElementDescriptionPlacement, useElementDescriptionContent } from '../hook';
import type { NodeId, ViewModelId } from 'balus-common';
import { useExclusiveTextEditing } from '@model-framework/text/editing-user';
import type { ElementDescription, ElementDescriptionRepository } from '../domain';
import { ElementDescriptionEditLockKey } from '../domain';
import { Callout, type ICalloutTarget } from '@model-framework/ui';
import { ElementDescriptionLayout } from './ElementDescriptionLayout';
import { ModelLayout } from '@view-model/models/sticky/layout';

type Props = {
  viewModelId: ViewModelId;
  elementId: NodeId;
  description: ElementDescription;
  readonly: boolean;
  elementRect: Rect;
  repository: ElementDescriptionRepository;
  /** しっぽ先端の交点計算に使うターゲット。省略時は elementRect（矩形）として計算。楕円ゾーンには EllipseTarget を渡す。 */
  targetCalloutTarget?: ICalloutTarget;
};

export const ElementDescriptionContent: React.FC<Props> = ({
  viewModelId,
  elementId,
  description,
  readonly,
  elementRect,
  repository,
  targetCalloutTarget,
}: Props) => {
  const { position, size, targetRect, setSize, onDragStart, onDrag, onDragEnd } = useElementDescriptionPlacement(
    elementRect,
    repository,
    description,
    readonly
  );

  const { content, onStartEdit, onEdit, onEndEdit } = useElementDescriptionContent(description, repository);

  const editLockKey = useMemo(() => ElementDescriptionEditLockKey.fromElementId(elementId), [elementId]);

  const { editing, startEdit, endEdit } = useExclusiveTextEditing(viewModelId, editLockKey, content, readonly);

  // 空文字はfalseなのでcontentだけ厳密にnullチェックする
  if (!position || !targetRect || content === null) return null;

  return (
    <Callout
      editing={editing}
      text={content}
      position={position}
      size={size}
      minHeight={ElementDescriptionLayout.initialSize().height}
      targetRect={targetRect}
      targetCalloutTarget={targetCalloutTarget}
      tailBaseLineLength={ModelLayout.GridSize}
      tailBaseLineMargin={ModelLayout.GridSize}
      onDragStart={onDragStart}
      onDrag={onDrag}
      onDragEnd={onDragEnd}
      onChangeSize={setSize}
      onStartEdit={onStartEdit}
      onEdit={onEdit}
      onEndEdit={onEndEdit}
      onBlur={endEdit}
      onDblClick={startEdit}
    />
  );
};
